import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { PlatformService } from '@core/services/platform.service';
import { SkynetService } from '@core/services/skynet.service';

/**
 * ONE chat, chosen by BO — the single entry point every Support control uses.
 *
 * ── THE CHOICE IS BO'S, AND IT IS EXCLUSIVE ───────────────────────────────
 *
 * `/livechat/skynetsettings` decides: configured, Skynet is the chat and BO's live-chat
 * script is never fetched; unconfigured, live chat starts and nothing of Skynet's is put in
 * the page. That either/or is the point — two launchers in the same corner is the failure it
 * prevents, and a brand with no settings row keeps LiveChat/Crisp exactly as it is today,
 * which is also the rollback.
 *
 * It is NOT a fallback chain: an unreachable settings endpoint answers false and live chat
 * runs, but a Skynet widget that fails to boot does not then fetch live chat. BO said which
 * provider this brand uses, and a second one appearing because the first was slow is worse
 * than a chat button that does nothing.
 *
 * ── WHY THE LIVE-CHAT HALF ARRIVES AS A CALLBACK ──────────────────────────
 *
 * There is no LiveChatService in this repo. The live-chat path is ~110 lines of jQuery string
 * surgery inside AppComponent (app.component.ts:1558), duplicated in the mobile tree, and
 * extracting it would mean refactoring the one piece of code this change must leave untouched
 * so it stays a working rollback. The caller passes it in instead. The decision still happens
 * here, once.
 */
@Injectable({
  providedIn: 'root'
})
export class SupportChatService {

  constructor(
    private skynet: SkynetService,
    private platformService: PlatformService,
    private translateService: TranslateService,
  ) { }

  /**
   * Called from AppComponent, after the first render — a third-party chat script has no
   * business competing with hydration.
   */
  start(startLiveChat: () => void): void {
    if (!this.platformService.isBrowser()) {
      return;
    }

    this.skynet.load().subscribe(active => {
      if (!active) {
        startLiveChat();

        return;
      }

      this.skynet.install();
      /* ⚠️ AND IDENTIFY A MEMBER WHO WAS ALREADY SIGNED IN.
         AuthEffects.login$ fires at rehydration (auth.http.service.ts dispatches Login on a
         stored session), which is BEFORE this request comes back — so on a reload the widget
         did not exist yet when signedIn() ran and the member would never be attached. This is
         also how an expired pass is re-minted on the next page load, which is the guide's own
         Flow D, with no timer anywhere. A guest no-ops. */
      this.skynet.identify();
    });
  }

  /**
   * A Support control was clicked. Answers FALSE when Skynet is the brand's chat and its
   * widget is not there to open.
   *
   * ⚠️ It does NOT fall back to live chat — not on principle, but because there is nothing to
   * fall back TO: BO chose Skynet for this brand, so the live-chat script was never fetched
   * and `openLiveChat()` would be a second silent no-op.
   */
  open(openLiveChat: () => void): boolean {
    if (this.skynet.isActive()) {
      if (this.skynet.open()) {
        return true;
      }

      /* ⚠️ AND IT SAYS SO, HERE — not by returning a boolean and hoping. BO chose Skynet for
         this brand, so the live-chat script was never fetched and openLiveChat() would be a
         second silent no-op. Skynet is configured and not answering (a 404 on widget.js, an
         ad-blocker, a CSP, a dead endpoint), so tell the visitor rather than leaving them
         clicking a control that does nothing — which is the failure this ticket is removing
         from the Contact Us link, not one to reintroduce. */
      Swal.fire({
        icon: 'error',
        text: this.translateService.instant('Chat is unavailable right now. Please try again later.'),
        confirmButtonText: this.translateService.instant('OK'),
      });

      return false;
    }

    openLiveChat();

    return true;
  }

  /**
   * A member signed in: attach them to whichever widget is running.
   *
   * Live chat needs nothing here — its visitor line is patched into the snippet before it
   * runs, and BO can key the script by member group, so it re-fetches through its own socket
   * rather than on the session.
   */
  signedIn(): void {
    this.skynet.identify();
  }

  /** And signed out: Skynet must forget them. See SkynetService.forget. */
  signedOut(): void {
    this.skynet.forget();
  }
}
